/**
 * Heartbeat minigame — a sine wave scrolls left. Player taps in sync
 * with peaks. Score = hit ratio over the game duration.
 */

import type { Minigame, MinigameState } from "./manager";

/** Time limit in milliseconds. */
const TIME_LIMIT = 8000;

/** Milliseconds between peaks. */
const PEAK_INTERVAL = 800;

/** Tolerance window for a "hit" (ms from nearest peak). */
const HIT_TOLERANCE = 150;

export class Heartbeat implements Minigame {
  readonly id = "heartbeat";
  readonly name = "Heartbeat";

  init(): MinigameState {
    return {
      elapsed: 0,
      hits: 0,
      misses: 0,
      peakCount: 0,
    };
  }

  tick(dt: number, state: MinigameState): MinigameState {
    const elapsed = (state.elapsed as number) + dt;
    const peakCount = Math.floor(elapsed / PEAK_INTERVAL);
    return { ...state, elapsed, peakCount };
  }

  onInput(state: MinigameState): MinigameState {
    const elapsed = state.elapsed as number;
    const hits = state.hits as number;
    const misses = state.misses as number;

    // Distance to the nearest peak
    const nearestPeakTime = Math.round(elapsed / PEAK_INTERVAL) * PEAK_INTERVAL;
    const dist = Math.abs(elapsed - nearestPeakTime);

    if (dist <= HIT_TOLERANCE) {
      return { ...state, hits: hits + 1 };
    }
    return { ...state, misses: misses + 1 };
  }

  isComplete(state: MinigameState): boolean {
    return (state.elapsed as number) >= TIME_LIMIT;
  }

  score(state: MinigameState): number {
    const peakCount = state.peakCount as number;
    const hits = state.hits as number;
    if (peakCount === 0) return 0;
    return Math.min(Math.round((hits / peakCount) * 100), 100);
  }

  render(width: number, height: number, state: MinigameState): string[] {
    const lines: string[] = [];
    const elapsed = state.elapsed as number;
    const hits = state.hits as number;
    const midRow = Math.floor(height / 2);

    for (let r = 0; r < height; r++) {
      const chars = Array.from({ length: width }, () => " ");

      if (r === midRow) {
        // Draw the sine wave across the width
        for (let c = 0; c < width; c++) {
          // Map column to time: current position is at the right edge
          const t = elapsed - (width - 1 - c) * 20; // 20ms per column
          if (t < 0) continue;

          const phase = (t / PEAK_INTERVAL) * Math.PI * 2;
          const amplitude = Math.sin(phase);

          // Check if this is near a peak (amplitude close to 1)
          if (amplitude > 0.95) {
            chars[c] = "^";
          } else if (amplitude > 0.3) {
            chars[c] = "~";
          } else if (amplitude < -0.3) {
            chars[c] = "~";
          } else {
            chars[c] = "-";
          }
        }

        // Timing marker at center
        const markerCol = Math.floor(width / 2);
        if (markerCol < width) chars[markerCol] = "|";
      } else if (r === midRow - 2) {
        // Draw wave above midline
        for (let c = 0; c < width; c++) {
          const t = elapsed - (width - 1 - c) * 20;
          if (t < 0) continue;
          const phase = (t / PEAK_INTERVAL) * Math.PI * 2;
          const amplitude = Math.sin(phase);
          if (amplitude > 0.7) {
            chars[c] = "~";
          }
        }
      } else if (r === midRow + 2) {
        // Draw wave below midline
        for (let c = 0; c < width; c++) {
          const t = elapsed - (width - 1 - c) * 20;
          if (t < 0) continue;
          const phase = (t / PEAK_INTERVAL) * Math.PI * 2;
          const amplitude = Math.sin(phase);
          if (amplitude < -0.7) {
            chars[c] = "~";
          }
        }
      } else if (r === 0) {
        // Hit counter at top
        const label = `Hits: ${hits}`;
        for (let i = 0; i < label.length && i < width; i++) {
          chars[i] = label[i];
        }
      }

      lines.push(chars.join(""));
    }

    return lines;
  }
}
